import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {Activity, RefreshCw, TriangleAlert} from 'lucide-react'
import {
  runCcpLocalDiagnostics,
  type CcpLocalDiagnosticsReport,
} from '../../services/ccpLocalDiagnostics'
import {
  getProbeCooldownRemainingMs,
  isProbeOnCooldown,
} from '../../services/softphoneRecoveryLock'
import {
  getConnectionFailureGuideline,
  type ConnectionFailureStep,
} from '../../services/connectionFailureGuidelines'
import type {ConnectSoftphoneErrorType} from '../../services/softphoneRecovery'

export type SoftphoneProbePhase = 'idle' | 'starting' | 'waiting' | 'closing' | 'failed'

/**
 * Set when diagnostics fail during this page load. A refresh clears it.
 * A later pass in the same page means Connect is still on the old network.
 */
let diagnosticFailedThisPageLoad = false

function diagnosticsPassedAfterFailure(report: CcpLocalDiagnosticsReport): boolean {
  if (!report.ok) {
    diagnosticFailedThisPageLoad = true
    return false
  }
  return diagnosticFailedThisPageLoad
}

interface ConnectSoftphoneRecoveryPanelProps {
  probePhase: SoftphoneProbePhase
  probeError: string | null
  onTestConnection: () => void
  /** Last softphone error type — drives the same “What to try” tips as Connection failed. */
  softphoneErrorType?: ConnectSoftphoneErrorType | null
  /** CCP agent is still starting — Test Connection stays disabled until it is ready. */
  agentInitializationPending?: boolean
}

const GUIDELINE_ICON_COLOR = 'rgb(163, 162, 66)'
const GUIDELINE_ICON_BOX = {width: '16px', height: '16px'} as const

function formatRetrySeconds(ms: number): string {
  return `${Math.max(1, Math.ceil(ms / 1000))}s`
}

function ChromeGuidelineIcon() {
  return (
    <span
      className='cw-connection-failed-step-icon flex-shrink-0'
      style={GUIDELINE_ICON_BOX}
      aria-hidden='true'
    >
      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 48 48'>
        <path
          fill='#4caf50'
          d='M44,24c0,11.044-8.956,20-20,20S4,35.044,4,24S12.956,4,24,4S44,12.956,44,24z'
        />
        <path
          fill='#ffc107'
          d='M24,4v20l8,4l-8.843,16c0.317,0,0.526,0,0.843,0c11.053,0,20-8.947,20-20S35.053,4,24,4z'
        />
        <path
          fill='#f44336'
          d='M41.84,15H24v13l-3-1L7.16,13.26H7.14C10.68,7.69,16.91,4,24,4C31.8,4,38.55,8.48,41.84,15z'
        />
        <path fill='#dd2c00' d='M7.158,13.264l8.843,14.862L21,27L7.158,13.264z' />
        <path fill='#558b2f' d='M23.157,44l8.934-16.059L28,25L23.157,44z' />
        <path fill='#f9a825' d='M41.865,15H24l-1.579,4.58L41.865,15z' />
        <path
          fill='#fff'
          d='M33,24c0,4.969-4.031,9-9,9s-9-4.031-9-9s4.031-9,9-9S33,19.031,33,24z'
        />
        <path
          fill='#2196f3'
          d='M31,24c0,3.867-3.133,7-7,7s-7-3.133-7-7s3.133-7,7-7S31,20.133,31,24z'
        />
      </svg>
    </span>
  )
}

function CompactStepRow({
  icon,
  text,
  tone = 'tip',
}: {
  icon: React.ReactNode
  text: string
  tone?: 'tip' | 'ok' | 'bad'
}) {
  return (
    <div className='cw-softphone-recovery-step'>
      {icon}
      <span
        className={clsx(
          'cw-softphone-recovery-step-text',
          tone === 'ok' && 'cw-softphone-recovery-step-ok',
          tone === 'bad' && 'cw-softphone-recovery-step-bad',
        )}
      >
        {text}
      </span>
    </div>
  )
}

function GuidelineStepRow({step}: {step: ConnectionFailureStep}) {
  return (
    <CompactStepRow
      tone='tip'
      text={step.text}
      icon={
        step.icon === 'chrome' ? (
          <ChromeGuidelineIcon />
        ) : (
          <i
            className={clsx('bi', step.icon, 'cw-connection-failed-step-icon flex-shrink-0')}
            style={{color: GUIDELINE_ICON_COLOR, ...GUIDELINE_ICON_BOX}}
            aria-hidden='true'
          />
        )
      }
    />
  )
}

export function ConnectSoftphoneRecoveryPanel({
  probePhase,
  probeError,
  onTestConnection,
  softphoneErrorType = null,
  agentInitializationPending = false,
}: ConnectSoftphoneRecoveryPanelProps) {
  const [diagnostics, setDiagnostics] = useState<CcpLocalDiagnosticsReport | null>(null)
  const [isDiagnosing, setIsDiagnosing] = useState(false)
  const [refreshRequiredBeforeProbe, setRefreshRequiredBeforeProbe] = useState(false)
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(() =>
    getProbeCooldownRemainingMs(),
  )

  const guideline = getConnectionFailureGuideline(softphoneErrorType ?? 'other')

  const acceptDiagnostics = (report: CcpLocalDiagnosticsReport) => {
    setDiagnostics(report)
    if (diagnosticsPassedAfterFailure(report)) {
      setRefreshRequiredBeforeProbe(true)
    }
  }

  const runDiagnostics = () => {
    setIsDiagnosing(true)
    void runCcpLocalDiagnostics()
      .then(acceptDiagnostics)
      .finally(() => setIsDiagnosing(false))
  }

  useEffect(() => {
    let cancelled = false
    setIsDiagnosing(true)
    void runCcpLocalDiagnostics()
      .then((report) => {
        if (!cancelled) acceptDiagnostics(report)
      })
      .finally(() => {
        if (!cancelled) setIsDiagnosing(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const tick = () => setCooldownRemainingMs(getProbeCooldownRemainingMs())
    tick()
    if (!isProbeOnCooldown()) return
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [probePhase])

  const diagnosticsOk = diagnostics?.ok === true
  const onCooldown = cooldownRemainingMs > 0
  const probeBusy =
    probePhase === 'starting' || probePhase === 'waiting' || probePhase === 'closing'
  const canTest =
    diagnosticsOk &&
    !onCooldown &&
    !probeBusy &&
    !isDiagnosing &&
    !agentInitializationPending &&
    !refreshRequiredBeforeProbe

  const buttonLabel = probeBusy
    ? 'Testing…'
    : agentInitializationPending
      ? 'Initializing…'
      : refreshRequiredBeforeProbe
        ? 'Refresh page to test'
        : onCooldown
          ? `Retry in ${formatRetrySeconds(cooldownRemainingMs)}`
          : 'Test Connection'

  return (
    <div
      className='cw-connection-failed cw-softphone-recovery'
      role='alert'
      aria-live='assertive'
      aria-labelledby='cw-softphone-recovery-title'
    >
      {/* 1. Header */}
      <div className='cw-connection-failed-hero cw-softphone-recovery-hero'>
        <TriangleAlert size={44} strokeWidth={2} color='#D7383C' aria-hidden='true' />
        <h4 id='cw-softphone-recovery-title' className='cw-connection-failed-title'>
          Softphone connection issue
        </h4>
        <p className='cw-connection-failed-summary'>
          You are marked offline due to connection failure. Please ensure you have a stable
          connection without any VPN or external plugin. Make a test call below to see if you
          are able to successfully connect.
        </p>
      </div>

      {/* 2. What to try — same middle flex slot as Connection failed panel */}
      <div className='cw-connection-failed-steps'>
        <div className='cw-connection-failed-steps-label'>What to try</div>
        {guideline.steps.map((step) => (
          <GuidelineStepRow key={step.id} step={step} />
        ))}
      </div>

      {/* 3. Test Connection */}
      <div className='cw-softphone-recovery-actions'>
        {refreshRequiredBeforeProbe && (
          <p className='cw-softphone-recovery-diag-hint' role='status'>
            Connection checks failed earlier on this page and now pass. Refresh this page so
            Connect can reconnect, then run Test Connection again.
          </p>
        )}
        {diagnostics && !diagnosticsOk && !isDiagnosing && !refreshRequiredBeforeProbe && (
          <p className='cw-softphone-recovery-diag-hint' role='status'>
            Both checks must pass before Test Connection unlocks. Fix microphone and network
            issues on your device first, then run diagnostics again.
          </p>
        )}
        <button
          type='button'
          className='btn w-100 fw-bold py-3'
          style={{
            backgroundColor: canTest ? '#D7383C' : '#9CA3AF',
            border: 'none',
            color: '#fff',
          }}
          disabled={!canTest}
          onClick={onTestConnection}
          aria-label={
            probeBusy
              ? 'Test connection in progress'
              : agentInitializationPending
                ? 'Test connection unavailable while Connect is initializing'
                : refreshRequiredBeforeProbe
                  ? 'Refresh the page before testing the softphone connection'
                  : onCooldown
                    ? `Retry in ${formatRetrySeconds(cooldownRemainingMs)}`
                    : 'Test softphone connection'
          }
        >
          {probeBusy ? (
            <>
              <RefreshCw size={16} className='cw-spin me-2' aria-hidden='true' />
              {buttonLabel}
            </>
          ) : (
            <>
              {!onCooldown && !refreshRequiredBeforeProbe && !agentInitializationPending && (
                <Activity size={16} className='me-2' aria-hidden='true' />
              )}
              {buttonLabel}
            </>
          )}
        </button>
      </div>

      {/* 4. Status + Run diagnostics */}
      <div className='cw-softphone-recovery-status'>
        <div className='cw-softphone-recovery-status-head'>
          <div className='cw-connection-failed-steps-label mb-0'>Status</div>
          <button
            type='button'
            className='cw-softphone-recovery-rerun'
            disabled={isDiagnosing || probeBusy}
            onClick={runDiagnostics}
            aria-label={isDiagnosing ? 'Running diagnostics' : 'Run diagnostics'}
          >
            <RefreshCw
              size={13}
              className={clsx(isDiagnosing && 'cw-spin')}
              aria-hidden='true'
            />
            {isDiagnosing ? 'Checking…' : 'Run diagnostics'}
          </button>
        </div>
        {isDiagnosing && !diagnostics ? (
          <CompactStepRow
            tone='tip'
            text='Running microphone and network checks…'
            icon={
              <RefreshCw
                size={14}
                className='cw-spin cw-connection-failed-step-icon flex-shrink-0'
                color='#A1A5B7'
                aria-hidden='true'
              />
            }
          />
        ) : (
          diagnostics?.checks.map((check) => (
            <CompactStepRow
              key={check.id}
              tone={check.ok ? 'ok' : 'bad'}
              text={check.message}
              icon={
                <i
                  className={`bi ${check.ok ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} cw-connection-failed-step-icon flex-shrink-0`}
                  style={{
                    color: check.ok ? '#60BB1A' : '#D7383C',
                    ...GUIDELINE_ICON_BOX,
                  }}
                  aria-hidden='true'
                />
              }
            />
          ))
        )}
        {probeError && (
          <p className='cw-softphone-recovery-error' role='status'>
            {probeError}
          </p>
        )}
      </div>
    </div>
  )
}
